<template>
    <div class="feature-flag-container">
        <o-bread-crumb>
            <template v-slot:pageName>Feature Flags</template>
            <template v-slot:pageDetail>System Settings</template>
            <template v-slot:pageDescription><br></template>
        </o-bread-crumb>

        <div v-if="validation" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
        </div>

        <!-- Header Card -->
        <div class="feature-flag-header-card">
            <div class="header-left">
                <h4 class="header-title">
                    <div class="icon-wrapper bg-primary-faint me-3">
                        <i class="fas fa-flag text-primary"></i>
                    </div>
                    Feature Flag Management
                </h4>
                <p class="header-subtitle ms-4">Control all features per corporate</p>
            </div>
            <div class="header-right" v-if="selectedCorporate">
                <button 
                    class="btn-secondary-action"
                    @click="viewAuditLogs"
                >
                    <i class="fas fa-history me-2"></i>
                    <span class="btn-text">Audit Logs</span>
                </button>
            </div>
        </div>

        <!-- Content Card -->
        <div class="feature-flag-content-card">
            <div v-if="page.isLoading" class="loading-state">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="loading-text">Loading settings...</p>
            </div>

            <div v-else>
                <!-- Corporate Selector -->
                <div class="settings-section">
                    <label class="section-label">Target Corporate</label>
                    <div class="custom-select-wrapper" :class="{ 'active': dropdownOpen }" ref="customSelectWrapper">
                        <div class="select-trigger" @click="toggleDropdown">
                            <div class="trigger-content">
                                <i class="fas fa-building text-primary me-3"></i>
                                <span class="selected-text" :class="{ 'text-muted': !selectedCorporate }">
                                    {{ selectedCorporateName }}
                                </span>
                            </div>
                            <i class="fas fa-chevron-down text-muted transition-arrow" :class="{ 'rotate-180': dropdownOpen }"></i>
                        </div>
                        
                        <div v-if="dropdownOpen" class="custom-options-list">
                            <!-- Search Input -->
                            <div class="dropdown-search-wrapper">
                                <i class="fas fa-search search-icon"></i>
                                <input 
                                    type="text" 
                                    v-model="searchQuery"
                                    class="dropdown-search-input"
                                    placeholder="Search corporate..."
                                    @click.stop
                                    ref="searchInput"
                                />
                                <i 
                                    v-if="searchQuery" 
                                    class="fas fa-times clear-icon"
                                    @click.stop="clearSearch"
                                ></i>
                            </div>
                            <!-- Options List -->
                            <div class="options-scroll-container">
                                <div 
                                    v-for="option in filteredCorporateOptions" 
                                    :key="option.value" 
                                    class="custom-option"
                                    :class="{ 'selected': option.value === selectedCorporate }"
                                    @click="selectOption(option)"
                                >
                                    {{ option.text }}
                                </div>
                                <div v-if="filteredCorporateOptions.length === 0" class="custom-option disabled">
                                    No Corporates Found
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Feature List -->
                <div class="feature-list" v-if="selectedCorporate">
                    <div class="feature-list-header">
                        <span class="header-label">FEATURES</span>
                    </div>

                    <!-- Benefit Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-gift text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Benefit Module</h4>
                                    <span class="status-dot" :class="isBenefitModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Benefit module for this corporate. Affects both admin and member dashboard.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isBenefitModuleActive"
                                    @change="toggleBenefitModule"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isBenefitModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Marketing Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-bullhorn text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Marketing Module</h4>
                                    <span class="status-dot" :class="isMarketingModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Marketing module (campaign management, referral tracking) for this corporate.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isMarketingModuleActive"
                                    @change="toggleMarketingModule"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isMarketingModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- New Authentication Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-shield-alt text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">New Authentication Module</h4>
                                    <span class="status-dot" :class="isNewAuthModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the separate dedicated endpoints for Member & Admin Backoffice login.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isNewAuthModuleActive"
                                    @change="toggleNewAuthModule"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isNewAuthModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Newsletter Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-newspaper text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Newsletter Module</h4>
                                    <span class="status-dot" :class="isNewsletterModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Newsletter module for this corporate. Affects both admin and member dashboard.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isNewsletterModuleActive"
                                    @change="toggleNewsletterModule"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isNewsletterModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Point Control Mode -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-coins text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Point Control Mode</h4>
                                    <span class="status-dot" :class="isPointControlActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable all point-related features for this corporate.</p>
                                <p v-if="hasChanges && statusChangeMessage" class="feature-change-info" v-html="statusChangeMessage"></p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isPointControlActive"
                                    @change="togglePointControl"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isPointControlActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Promotions Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-tags text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Promotions Module</h4>
                                    <span class="status-dot" :class="isPromotionsModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Promotions module for this corporate. Affects admin promotions page and member offers/deals sections.</p>
                                <p v-if="isPromotionsForceDisabled" class="feature-change-info">
                                    <i class="fas fa-info-circle me-1"></i>Automatically disabled because both <strong>Point Control</strong> and <strong>Benefit Module</strong> are NONACTIVE.
                                </p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch" :class="{ 'toggle-disabled': isPromotionsForceDisabled }">
                                <input 
                                    type="checkbox" 
                                    :checked="isPromotionsModuleActive"
                                    @change="togglePromotionsModule"
                                    :disabled="isPromotionsForceDisabled"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isPromotionsModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Threshold Setting -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-sliders-h text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Threshold Setting</h4>
                                    <span class="status-dot" :class="isThresholdSettingActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Threshold Setting (member tier warning notification) for this corporate.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isThresholdSettingActive"
                                    @change="toggleThresholdSetting"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isThresholdSettingActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Transaction Report Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-chart-bar text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Transaction Report Module</h4>
                                    <span class="status-dot" :class="isTransactionReportActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Transaction Report module for this corporate.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isTransactionReportActive"
                                    @change="toggleTransactionReport"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isTransactionReportActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Branch Product Management Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-box text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Branch Product Management</h4>
                                    <span class="status-dot" :class="isBranchProductActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable product management at the branch level.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isBranchProductActive"
                                    @change="toggleBranchProduct"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isBranchProductActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Merchant Product Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-store text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Merchant Product</h4>
                                    <span class="status-dot" :class="isMerchantProductActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable product management at the merchant level.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isMerchantProductActive"
                                    @change="toggleMerchantProduct"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isMerchantProductActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Access Token & Auth URL Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-key text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Access Token & Auth URL</h4>
                                    <span class="status-dot" :class="isMerchantAccessTokenActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable merchant access token and auth URL features.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isMerchantAccessTokenActive"
                                    @change="toggleMerchantAccessToken"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isMerchantAccessTokenActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Voucher Gift Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-ticket-alt text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Voucher Gift Module</h4>
                                    <span class="status-dot" :class="isVoucherGiftActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable voucher gifting capabilities.</p>
                                <p v-if="isVoucherGiftForceDisabled" class="feature-change-info">
                                    <i class="fas fa-info-circle me-1"></i>Automatically disabled because <strong>Point Control</strong> is NONACTIVE.
                                </p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch" :class="{ 'toggle-disabled': isVoucherGiftForceDisabled }">
                                <input 
                                    type="checkbox" 
                                    :checked="isVoucherGiftActive"
                                    @change="toggleVoucherGift"
                                    :disabled="isVoucherGiftForceDisabled"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isVoucherGiftActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Booking Engine Module -->
                    <div class="feature-item">
                        <div class="feature-info">
                            <div class="icon-wrapper bg-primary-faint">
                                <i class="fas fa-hotel text-primary"></i>
                            </div>
                            <div class="text-content">
                                <div class="title-row">
                                    <h4 class="feature-title">Booking Engine Module</h4>
                                    <span class="status-dot" :class="isBookingModuleActive ? 'active' : 'inactive'"></span>
                                </div>
                                <p class="feature-desc">Switch to enable or disable the Booking Engine integration and Our Properties section on member dashboard.</p>
                            </div>
                        </div>
                        <div class="feature-action">
                            <label class="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    :checked="isBookingModuleActive"
                                    @change="toggleBookingModule"
                                >
                                <span class="slider round"></span>
                                <span class="toggle-label">{{ isBookingModuleActive ? 'Active' : 'Inactive' }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Footer Actions -->
                <div v-if="hasChanges" class="feature-flag-footer">
                    <button class="btn-discard" @click="discardChanges">
                        <i class="fas fa-times me-2"></i>
                        Discard Changes
                    </button>
                    <button class="btn-save" @click="saveChanges">
                        <i class="fas fa-save me-2"></i>
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { OBreadCrumb } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'

export default {
    name: 'FeatureFlagAdmin',
    components: {
        OBreadCrumb,
    },
    setup() {
        const store = useStore()
        const router = useRouter()
        const { showToast } = useToast()

        const page = reactive({
            isLoading: false
        })

        const selectedCorporate = ref(null)
        const currentFlag = ref('DECENTRALIZE')
        const localFlag = ref('DECENTRALIZE')
        const currentNewAuthFlag = ref('NONACTIVE')
        const localNewAuthFlag = ref('NONACTIVE')
        const currentMarketingFlag = ref('ACTIVE')
        const localMarketingFlag = ref('ACTIVE')
        const currentTransactionReportFlag = ref('ACTIVE')
        const localTransactionReportFlag = ref('ACTIVE')
        const currentThresholdSettingFlag = ref('ACTIVE')
        const localThresholdSettingFlag = ref('ACTIVE')
        const currentNewsletterFlag = ref('ACTIVE')
        const localNewsletterFlag = ref('ACTIVE')
        const currentBenefitFlag = ref('ACTIVE')
        const localBenefitFlag = ref('ACTIVE')
        const currentPromotionsFlag = ref('ACTIVE')
        const localPromotionsFlag = ref('ACTIVE')
        const currentBranchProductFlag = ref('ACTIVE')
        const localBranchProductFlag = ref('ACTIVE')
        const currentMerchantProductFlag = ref('ACTIVE')
        const localMerchantProductFlag = ref('ACTIVE')
        const currentMerchantAccessTokenFlag = ref('NONACTIVE')
        const localMerchantAccessTokenFlag = ref('NONACTIVE')
        const currentVoucherGiftFlag = ref('ACTIVE')
        const localVoucherGiftFlag = ref('ACTIVE')
        const currentBookingModuleFlag = ref('ACTIVE')
        const localBookingModuleFlag = ref('ACTIVE')
        const validation = ref('')
        const dropdownOpen = ref(false)
        const customSelectWrapper = ref(null)
        const searchQuery = ref('')
        const searchInput = ref(null)

        const corporates = computed(() => store.state.Corporate?.corporates || [])
        
        const corporateOptions = computed(() => 
            corporates.value.map(corp => ({
                value: corp.id,
                text: corp.name
            }))
        )

        // Filtered corporate options based on search query
        const filteredCorporateOptions = computed(() => {
            if (!searchQuery.value.trim()) {
                return corporateOptions.value
            }
            const query = searchQuery.value.toLowerCase().trim()
            return corporateOptions.value.filter(option => 
                option.text.toLowerCase().includes(query)
            )
        })

        const selectedCorporateName = computed(() => {
            const found = corporateOptions.value.find(c => c.value === selectedCorporate.value)
            return found ? found.text : 'Select Corporate'
        })

        // Checked if localFlag is DECENTRALIZE or CENTRALIZE
        const isPointControlActive = computed(() => {
            return localFlag.value === 'CENTRALIZE' || localFlag.value === 'DECENTRALIZE'
        })

        const isNewAuthModuleActive = computed(() => {
            return localNewAuthFlag.value === 'ACTIVE'
        })

        const isMarketingModuleActive = computed(() => {
            return localMarketingFlag.value === 'ACTIVE'
        })

        const isTransactionReportActive = computed(() => {
            return localTransactionReportFlag.value === 'ACTIVE'
        })

        const isThresholdSettingActive = computed(() => {
            return localThresholdSettingFlag.value === 'ACTIVE'
        })

        const isNewsletterModuleActive = computed(() => {
            return localNewsletterFlag.value === 'ACTIVE'
        })

        const isBenefitModuleActive = computed(() => {
            return localBenefitFlag.value === 'ACTIVE'
        })

        const isPromotionsModuleActive = computed(() => {
            return localPromotionsFlag.value === 'ACTIVE'
        })

        const isBranchProductActive = computed(() => {
            return localBranchProductFlag.value === 'ACTIVE'
        })

        const isMerchantProductActive = computed(() => {
            return localMerchantProductFlag.value === 'ACTIVE'
        })

        const isMerchantAccessTokenActive = computed(() => {
            return localMerchantAccessTokenFlag.value === 'ACTIVE'
        })

        const isVoucherGiftActive = computed(() => {
            return localVoucherGiftFlag.value === 'ACTIVE'
        })

        const isBookingModuleActive = computed(() => {
            return localBookingModuleFlag.value === 'ACTIVE'
        })

        // Promotions is force-disabled when both point control AND benefit module are NONACTIVE
        const isPromotionsForceDisabled = computed(() => {
            return !isPointControlActive.value && localBenefitFlag.value === 'NONACTIVE'
        })

        const isVoucherGiftForceDisabled = computed(() => {
            return !isPointControlActive.value
        })

        // Check if there are unsaved changes
        const hasChanges = computed(() => {
            return (currentFlag.value !== localFlag.value) || (currentNewAuthFlag.value !== localNewAuthFlag.value) || (currentMarketingFlag.value !== localMarketingFlag.value) || (currentTransactionReportFlag.value !== localTransactionReportFlag.value) || (currentThresholdSettingFlag.value !== localThresholdSettingFlag.value) || (currentNewsletterFlag.value !== localNewsletterFlag.value) || (currentBenefitFlag.value !== localBenefitFlag.value) || (currentPromotionsFlag.value !== localPromotionsFlag.value) || (currentBranchProductFlag.value !== localBranchProductFlag.value) || (currentMerchantProductFlag.value !== localMerchantProductFlag.value) || (currentMerchantAccessTokenFlag.value !== localMerchantAccessTokenFlag.value) || (currentVoucherGiftFlag.value !== localVoucherGiftFlag.value) || (currentBookingModuleFlag.value !== localBookingModuleFlag.value)
        })

        // Auto-disable promotions when force-disabled condition is met
        watch(isPromotionsForceDisabled, (newVal) => {
            if (newVal) {
                localPromotionsFlag.value = 'NONACTIVE'
            }
        })

        // Auto-disable voucher gift when force-disabled condition is met
        watch(isVoucherGiftForceDisabled, (newVal) => {
            if (newVal) {
                localVoucherGiftFlag.value = 'NONACTIVE'
                showToast({
                    type: 'warning',
                    title: 'Auto-Disabled',
                    message: 'Voucher Gift has been disabled due to missing point control.'
                })
            }
        })

        function handleClickOutside(event) {
            if (customSelectWrapper.value && !customSelectWrapper.value.contains(event.target)) {
                dropdownOpen.value = false
            }
        }

        onMounted(() => {
            document.addEventListener('click', handleClickOutside)
            loadCorporates()
            
            store.watch(
                () => selectedCorporate.value,
                (newVal) => {
                    if (newVal) {
                        loadFeatureFlag()
                    }
                }
            )
        })

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside)
        })

        // info message for the pending change
        const statusChangeMessage = computed(() => {
            if (currentFlag.value === localFlag.value) return ''

            if (localFlag.value === 'NONACTIVE') {
                return `Changing to <strong>NONACTIVE</strong> will set all Point Plans to zero. Don't worry, the current values will be backed up and can be automatically restored when you switch back.`
            } else if (currentFlag.value === 'NONACTIVE' && localFlag.value === 'DECENTRALIZE') { 
                return `Changing to <strong>Active</strong> will restore your Point Plan values from backup if available.`
            }
            return ''
        })

        async function loadCorporates() {
            page.isLoading = true
            validation.value = ''

            try {
                // Use readForDropdown with limit 50 instead of default read with limit 10
                await store.dispatch('Corporate/readForDropdown')
            } catch (error) {
                validation.value = error.response?.data?.message || 'Failed to load corporates'
            } finally {
                page.isLoading = false
            }
        }

        async function loadFeatureFlag() {
            if (!selectedCorporate.value) return

            page.isLoading = true
            validation.value = ''
            dropdownOpen.value = false

            try {
                await store.dispatch('FeatureFlag/fetchFeatureFlag', selectedCorporate.value)
                const flags = store.state.FeatureFlag
                
                const fetchedFlag = flags.pointControl?.flag || 'DECENTRALIZE'
                currentFlag.value = fetchedFlag
                localFlag.value = fetchedFlag
                
                const fetchedAuthFlag = flags.newAuthModule?.flag || 'NONACTIVE'
                currentNewAuthFlag.value = fetchedAuthFlag
                localNewAuthFlag.value = fetchedAuthFlag

                const fetchedMarketingFlag = flags.marketingModule?.flag || 'ACTIVE'
                currentMarketingFlag.value = fetchedMarketingFlag
                localMarketingFlag.value = fetchedMarketingFlag

                const fetchedTransactionReportFlag = flags.transactionReportModule?.flag || 'ACTIVE'
                currentTransactionReportFlag.value = fetchedTransactionReportFlag
                localTransactionReportFlag.value = fetchedTransactionReportFlag

                const fetchedThresholdSettingFlag = flags.thresholdSetting?.flag || 'ACTIVE'
                currentThresholdSettingFlag.value = fetchedThresholdSettingFlag
                localThresholdSettingFlag.value = fetchedThresholdSettingFlag

                const fetchedNewsletterFlag = flags.newsletterModule?.flag || 'ACTIVE'
                currentNewsletterFlag.value = fetchedNewsletterFlag
                localNewsletterFlag.value = fetchedNewsletterFlag

                const fetchedBenefitFlag = flags.benefitModule?.flag || 'ACTIVE'
                currentBenefitFlag.value = fetchedBenefitFlag
                localBenefitFlag.value = fetchedBenefitFlag

                const fetchedPromotionsFlag = flags.promotionsModule?.flag || 'ACTIVE'
                currentPromotionsFlag.value = fetchedPromotionsFlag
                localPromotionsFlag.value = fetchedPromotionsFlag

                const fetchedBranchProductFlag = flags.branchProductManagement?.flag || 'ACTIVE'
                currentBranchProductFlag.value = fetchedBranchProductFlag
                localBranchProductFlag.value = fetchedBranchProductFlag

                const fetchedMerchantProductFlag = flags.merchantProduct?.flag || 'ACTIVE'
                currentMerchantProductFlag.value = fetchedMerchantProductFlag
                localMerchantProductFlag.value = fetchedMerchantProductFlag

                const fetchedMerchantAccessTokenFlag = flags.merchantAccessToken?.flag || 'NONACTIVE'
                currentMerchantAccessTokenFlag.value = fetchedMerchantAccessTokenFlag
                localMerchantAccessTokenFlag.value = fetchedMerchantAccessTokenFlag

                const fetchedVoucherGiftFlag = flags.voucherGift?.flag || 'ACTIVE'
                currentVoucherGiftFlag.value = fetchedVoucherGiftFlag
                localVoucherGiftFlag.value = fetchedVoucherGiftFlag

                const fetchedBookingFlag = flags.bookingModule?.flag || 'ACTIVE'
                currentBookingModuleFlag.value = fetchedBookingFlag
                localBookingModuleFlag.value = fetchedBookingFlag
            } catch (error) {
                currentFlag.value = 'DECENTRALIZE'
                localFlag.value = 'DECENTRALIZE'
                currentNewAuthFlag.value = 'NONACTIVE'
                localNewAuthFlag.value = 'NONACTIVE'
                currentMarketingFlag.value = 'ACTIVE'
                localMarketingFlag.value = 'ACTIVE'
                currentTransactionReportFlag.value = 'ACTIVE'
                localTransactionReportFlag.value = 'ACTIVE'
                currentThresholdSettingFlag.value = 'ACTIVE'
                localThresholdSettingFlag.value = 'ACTIVE'
                currentNewsletterFlag.value = 'ACTIVE'
                localNewsletterFlag.value = 'ACTIVE'
                currentBenefitFlag.value = 'ACTIVE'
                localBenefitFlag.value = 'ACTIVE'
                currentPromotionsFlag.value = 'ACTIVE'
                localPromotionsFlag.value = 'ACTIVE'
                currentBranchProductFlag.value = 'ACTIVE'
                localBranchProductFlag.value = 'ACTIVE'
                currentMerchantProductFlag.value = 'ACTIVE'
                localMerchantProductFlag.value = 'ACTIVE'
                currentMerchantAccessTokenFlag.value = 'NONACTIVE'
                localMerchantAccessTokenFlag.value = 'NONACTIVE'
                currentVoucherGiftFlag.value = 'ACTIVE'
                localVoucherGiftFlag.value = 'ACTIVE'
                currentBookingModuleFlag.value = 'ACTIVE'
                localBookingModuleFlag.value = 'ACTIVE'
            } finally {
                page.isLoading = false
            }
        }

        function togglePointControl(event) {
            const isChecked = event.target.checked
            // ON -> DECENTRALIZE, OFF -> NONACTIVE
            localFlag.value = isChecked ? 'DECENTRALIZE' : 'NONACTIVE'
        }

        function toggleNewAuthModule(event) {
            const isChecked = event.target.checked
            localNewAuthFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleMarketingModule(event) {
            const isChecked = event.target.checked
            localMarketingFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleTransactionReport(event) {
            const isChecked = event.target.checked
            localTransactionReportFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleThresholdSetting(event) {
            const isChecked = event.target.checked
            localThresholdSettingFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleNewsletterModule(event) {
            const isChecked = event.target.checked
            localNewsletterFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleBenefitModule(event) {
            const isChecked = event.target.checked
            localBenefitFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function togglePromotionsModule(event) {
            const isChecked = event.target.checked
            localPromotionsFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleBranchProduct(event) {
            const isChecked = event.target.checked
            localBranchProductFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
            checkProductFlagsWarning()
        }

        function toggleMerchantProduct(event) {
            const isChecked = event.target.checked
            localMerchantProductFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
            checkProductFlagsWarning()
        }

        function toggleMerchantAccessToken(event) {
            const isChecked = event.target.checked
            localMerchantAccessTokenFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleVoucherGift(event) {
            const isChecked = event.target.checked
            localVoucherGiftFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function toggleBookingModule(event) {
            const isChecked = event.target.checked
            localBookingModuleFlag.value = isChecked ? 'ACTIVE' : 'NONACTIVE'
        }

        function checkProductFlagsWarning() {
            if (localBranchProductFlag.value === 'NONACTIVE' && localMerchantProductFlag.value === 'NONACTIVE') {
                showToast({
                    type: 'warning',
                    title: 'POS Membership Impact',
                    message: 'Disabling both Branch and Merchant Product will make POS Membership unusable for creating transactions.'
                })
            }
        }

        function discardChanges() {
            localFlag.value = currentFlag.value
            localNewAuthFlag.value = currentNewAuthFlag.value
            localMarketingFlag.value = currentMarketingFlag.value
            localTransactionReportFlag.value = currentTransactionReportFlag.value
            localThresholdSettingFlag.value = currentThresholdSettingFlag.value
            localNewsletterFlag.value = currentNewsletterFlag.value
            localBenefitFlag.value = currentBenefitFlag.value
            localPromotionsFlag.value = currentPromotionsFlag.value
            localBranchProductFlag.value = currentBranchProductFlag.value
            localMerchantProductFlag.value = currentMerchantProductFlag.value
            localMerchantAccessTokenFlag.value = currentMerchantAccessTokenFlag.value
            localVoucherGiftFlag.value = currentVoucherGiftFlag.value
            localBookingModuleFlag.value = currentBookingModuleFlag.value
        }

        async function saveChanges() {
            page.isLoading = true
            validation.value = ''

            try {
                let anySaved = false

                if (currentFlag.value !== localFlag.value) {
                    const oldFlag = currentFlag.value
                    const newFlag = localFlag.value
                    
                    const hadBackup = oldFlag === 'NONACTIVE' && 
                                     store.state.FeatureFlag.pointControl?.metadata?.backup_point_plans
                    
                    const currentMetadata = store.state.FeatureFlag.pointControl?.metadata || {}
                    const preservedMetadata = {
                        point_value_enabled: currentMetadata.point_value_enabled,
                        point_value_rate: currentMetadata.point_value_rate
                    }
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'point_control',
                        flag: newFlag,
                        metadata: preservedMetadata
                    })

                    currentFlag.value = newFlag
                    anySaved = true
                    
                    if (newFlag === 'NONACTIVE') {
                        showToast({
                            type: 'success',
                            title: 'Feature Disabled',
                            message: 'Point Control disabled. Values backed up.',
                        })
                    } else {
                         if (hadBackup) {
                            showToast({
                                type: 'success',
                                title: 'Feature Enabled',
                                message: 'Point Control enabled. Values restored from backup.',
                            })
                         } else {
                            showToast({
                                type: 'success',
                                title: 'Feature Enabled',
                                message: 'Point Control enabled.',
                            })
                         }
                    }
                }

                if (currentNewAuthFlag.value !== localNewAuthFlag.value) {
                    const newAuthFlag = localNewAuthFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'new_auth_module',
                        flag: newAuthFlag,
                        metadata: {}
                    })

                    currentNewAuthFlag.value = newAuthFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newAuthFlag === 'ACTIVE' ? 'New Authentication Module enabled.' : 'New Authentication Module disabled.',
                    })
                }

                if (currentMarketingFlag.value !== localMarketingFlag.value) {
                    const newMarketingFlag = localMarketingFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'marketing_module',
                        flag: newMarketingFlag,
                        metadata: {}
                    })

                    currentMarketingFlag.value = newMarketingFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newMarketingFlag === 'ACTIVE' ? 'Marketing Module enabled.' : 'Marketing Module disabled.',
                    })
                }

                if (currentTransactionReportFlag.value !== localTransactionReportFlag.value) {
                    const newFlag = localTransactionReportFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'transaction_report_module',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentTransactionReportFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Transaction Report Module enabled.' : 'Transaction Report Module disabled.',
                    })
                }

                if (currentThresholdSettingFlag.value !== localThresholdSettingFlag.value) {
                    const newFlag = localThresholdSettingFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'threshold_setting',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentThresholdSettingFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Threshold Setting enabled.' : 'Threshold Setting disabled.',
                    })
                }

                if (currentNewsletterFlag.value !== localNewsletterFlag.value) {
                    const newFlag = localNewsletterFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'newsletter_module',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentNewsletterFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Newsletter Module enabled.' : 'Newsletter Module disabled.',
                    })
                }

                if (currentBenefitFlag.value !== localBenefitFlag.value) {
                    const newFlag = localBenefitFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'benefit_module',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentBenefitFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Benefit Module enabled.' : 'Benefit Module disabled.',
                    })
                }

                if (currentPromotionsFlag.value !== localPromotionsFlag.value) {
                    const newFlag = localPromotionsFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'promotions_module',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentPromotionsFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Promotions Module enabled.' : 'Promotions Module disabled.',
                    })
                }

                if (currentBranchProductFlag.value !== localBranchProductFlag.value) {
                    const newFlag = localBranchProductFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'branch_product_management',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentBranchProductFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Branch Product Management enabled.' : 'Branch Product Management disabled.',
                    })
                }

                if (currentMerchantProductFlag.value !== localMerchantProductFlag.value) {
                    const newFlag = localMerchantProductFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'merchant_product',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentMerchantProductFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Merchant Product enabled.' : 'Merchant Product disabled.',
                    })
                }

                if (currentMerchantAccessTokenFlag.value !== localMerchantAccessTokenFlag.value) {
                    const newFlag = localMerchantAccessTokenFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'merchant_access_token',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentMerchantAccessTokenFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Access Token & Auth URL enabled.' : 'Access Token & Auth URL disabled.',
                    })
                }

                if (currentVoucherGiftFlag.value !== localVoucherGiftFlag.value) {
                    const newFlag = localVoucherGiftFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'voucher_gift',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentVoucherGiftFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Voucher Gift Module enabled.' : 'Voucher Gift Module disabled.',
                    })
                }

                if (currentBookingModuleFlag.value !== localBookingModuleFlag.value) {
                    const newFlag = localBookingModuleFlag.value
                    
                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: selectedCorporate.value,
                        feature: 'booking_module',
                        flag: newFlag,
                        metadata: {}
                    })

                    currentBookingModuleFlag.value = newFlag
                    anySaved = true
                    
                    showToast({
                        type: 'success',
                        title: 'Feature Updated',
                        message: newFlag === 'ACTIVE' ? 'Booking Engine Module enabled.' : 'Booking Engine Module disabled.',
                    })
                }

                if (anySaved) {
                    await loadFeatureFlag()
                }

            } catch (error) {
                validation.value = error.response?.data?.message || 'Failed to update feature flag'
                showToast({
                    type: 'error',
                    title: 'Update Failed',
                    message: validation.value,
                })
            } finally {
                page.isLoading = false
            }
        }

        function toggleDropdown() {
            dropdownOpen.value = !dropdownOpen.value
            // Focus search input when dropdown opens
            if (dropdownOpen.value) {
                setTimeout(() => {
                    searchInput.value?.focus()
                }, 100)
            } else {
                // Clear search when dropdown closes
                searchQuery.value = ''
            }
        }

        function selectOption(option) {
            selectedCorporate.value = option.value
            dropdownOpen.value = false
            searchQuery.value = ''
        }

        function clearSearch() {
            searchQuery.value = ''
            searchInput.value?.focus()
        }

        function viewAuditLogs() {
            router.push(`/featureflag/auditlog/${selectedCorporate.value}`)
        }

        return {
            page,
            selectedCorporate,
            isPointControlActive,
            isNewAuthModuleActive,
            isMarketingModuleActive,
            isTransactionReportActive,
            isThresholdSettingActive,
            isNewsletterModuleActive,
            isBenefitModuleActive,
            isPromotionsModuleActive,
            isBranchProductActive,
            isMerchantProductActive,
            isMerchantAccessTokenActive,
            isVoucherGiftActive,
            isPromotionsForceDisabled,
            isVoucherGiftForceDisabled,
            hasChanges,
            validation,
            corporateOptions,
            filteredCorporateOptions,
            selectedCorporateName,
            dropdownOpen,
            searchQuery,
            searchInput,
            togglePointControl,
            toggleNewAuthModule,
            toggleMarketingModule,
            toggleTransactionReport,
            toggleThresholdSetting,
            toggleNewsletterModule,
            toggleBenefitModule,
            togglePromotionsModule,
            toggleBranchProduct,
            toggleMerchantProduct,
            toggleMerchantAccessToken,
            toggleVoucherGift,
            toggleBookingModule,
            isBookingModuleActive,
            saveChanges,
            discardChanges,
            viewAuditLogs,
            toggleDropdown,
            selectOption,
            clearSearch,
            statusChangeMessage,
            customSelectWrapper
        }
    }
}
</script>

<style scoped>
/* Container & Variables */
.feature-flag-container {
    padding: 16px;
    padding-right: 20px;
    max-width: 60dvw;
    margin-left: auto;
    margin-right: auto;
    --primary-color: #ee4b2b;
    --primary-color-dark: #d64747;
    --success-color: #10b981;
    --danger-color: #ef4444;
    --warning-color: #f59e0b;
    --text-main: #0f172a;
    --text-muted: #64748b;
    --border-color: #e2e8f0;
    --bg-light: #f8f6f6;
}

/* Header Card */
.feature-flag-header-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid var(--border-color);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    margin-bottom: 1rem; /* Space between cards */
}

/* Content Card */
.feature-flag-content-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid var(--border-color);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Header */
.feature-flag-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid var(--border-color);
    background-color: #fff;
}

.header-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-main);
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
    padding-left: 2rem;
}

.btn-secondary-action {
    background: #fff;
    color: var(--text-muted);
    border: 1px solid var(--border-color);
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
}

.btn-secondary-action:hover {
    color: var(--bg-light);
    background-color: var(--primary-color);
}

/* Content */
.feature-flag-content {
    padding: 0;
}

.loading-state {
    padding: 4rem 2rem;
    text-align: center;
}

/* Settings Section */
.settings-section {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid var(--border-color);
    background: #fff;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.section-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-main);
    margin-bottom: 0.75rem;
}

.custom-select-wrapper {
    position: relative;
    width: 100%;
}

.select-trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    background-color: var(--bg-light);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.select-trigger:hover {
    background-color: #fff;
    border-color: var(--border-color);
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.custom-select-wrapper.active .select-trigger {
    background-color: #fff;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 4px rgba(238, 75, 43, 0.1);
}

.trigger-content {
    display: flex;
    align-items: center;
}

.selected-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-main);
}

.selected-text.text-muted {
    color: var(--text-muted);
}

.transition-arrow {
    transition: transform 0.2s ease;
}

.rotate-180 {
    transform: rotate(180deg);
}

.custom-options-list {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background-color: #fff;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    max-height: 320px;
    overflow: hidden;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
}

.custom-option {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-main);
    transition: background-color 0.1s ease;
}

.custom-option:hover {
    background-color: var(--bg-light);
}

.custom-option.selected {
    background-color: rgba(238, 75, 43, 0.05);
    color: var(--primary-color);
    font-weight: 600;
}

.custom-option.disabled {
    color: var(--text-muted);
    cursor: not-allowed;
}

/* Dropdown Search Styles */
.dropdown-search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 0.5rem;
    flex-shrink: 0;
}

.search-icon {
    position: absolute;
    left: 1rem;
    color: var(--text-muted);
    font-size: 0.875rem;
}

.dropdown-search-input {
    width: 100%;
    padding: 0.625rem 2rem 0.625rem 2.25rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 0.875rem;
    color: var(--text-main);
    background-color: var(--bg-light);
    transition: all 0.2s ease;
}

.dropdown-search-input:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(238, 75, 43, 0.1);
}

.dropdown-search-input::placeholder {
    color: var(--text-muted);
}

.clear-icon {
    position: absolute;
    right: 1rem;
    color: var(--text-muted);
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.clear-icon:hover {
    color: var(--primary-color);
    background-color: rgba(238, 75, 43, 0.1);
}

.options-scroll-container {
    overflow-y: auto;
    max-height: 220px;
    flex: 1;
}

/* Feature List */
.feature-list {
    display: flex;
    flex-direction: column;
}

.feature-list-header {
    padding: 1rem 2rem;
    background: var(--bg-light);
    border-bottom: 1px solid var(--border-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

/* Feature Item */
.feature-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2rem 2rem;
    border-bottom: 1px solid var(--border-color);
    transition: background-color 0.2s ease;
}

.feature-item:last-child {
    border-bottom: none;
}

.feature-item:hover {
    background-color: rgba(249, 250, 251, 0.5);
}

.feature-info {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex: 1;
}

.icon-wrapper {
    background-color: rgba(238, 75, 43, 0.1); /* Primary faint #ee4b2b with opacity */
    padding: 0.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
}

.icon-wrapper.bg-gray {
    background-color: #f3f4f6;
}

.icon-wrapper.bg-primary-faint {
    background-color: rgba(238, 75, 43, 0.1);
}

.text-primary { color: var(--primary-color); }
.text-muted { color: var(--text-muted); }

.text-content {
    flex: 1;
}

.title-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
}

.feature-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-main);
    margin: 0;
}

.feature-desc {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
    max-width: 600px;
}

.feature-change-info {
    font-size: 0.8125rem; /* 13px */
    color: var(--warning-color);
    margin-top: 0.5rem;
    background-color: #fffbeb; /* Light yellow background */
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    border: 1px solid #fcd34d;
    display: inline-block;
    max-width: 75%;
}

/* Status Dot */
.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.status-dot.active {
    background-color: var(--success-color);
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.status-dot.inactive {
    background-color: #d1d5db; /* Gray */
}

/* Toggle Switch */
.toggle-switch {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    position: relative;
    user-select: none;
}

.toggle-switch input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.slider {
    position: relative;
    display: inline-block;
    width: 56px;
    height: 32px;
    background-color: #e5e7eb;
    transition: .3s;
    border-radius: 34px;
}

.slider:before {
    position: absolute;
    content: "";
    height: 24px;
    width: 24px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

input:checked + .slider {
    background-color: var(--primary-color);
}

input:checked + .slider:before {
    transform: translateX(24px);
}

/* Disabled state */
input:disabled + .slider {
    background-color: #f3f4f6;
    cursor: not-allowed;
}

.toggle-label {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--text-muted);
    min-width: 60px;
}

.toggle-switch:hover .toggle-label {
    color: var(--text-main);
}

.toggle-switch.toggle-disabled {
    opacity: 0.5;
    pointer-events: none;
}

/* Footer */
/* Footer */
.feature-flag-footer {
    padding: 1rem 2rem;
    background: var(--bg-light);
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

.btn-discard {
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-muted);
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
}

.btn-discard:hover {
    color: var(--text-main);
    background: #fff;
    border-color: var(--border-color);
}

.btn-save {
    padding: 0.625rem 2rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 700;
    color: #fff;
    background: var(--primary-color);
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 6px -1px rgba(238, 75, 43, 0.2);
    display: flex;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.btn-save:hover {
    background: var(--primary-color-dark);
    transform: translateY(-1px);
    box-shadow: 0 6px 8px -1px rgba(238, 75, 43, 0.3);
}

.btn-save:active {
    transform: translateY(0);
}
</style>
